import { I18n } from "@assets/strings";
import React, { useEffect, useRef, useState } from "react";
import { useHistory } from "@core/routing/useHistory";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { useServices } from "@core/servicesContext";
import { Carousel } from "@domain/carousel/carousel";
import { useCarousel } from "@domain/carousel/hooks";
import { useCurrentProfile, useIsPerson } from "@domain/currentProfile/hooks";
import { useIsMobile, useOneCallTimer, useWindowSize } from "@utils/hooks";
import { AppAuthIncentive } from "@ui/components/appAuthIncentive";
import { ArrowIcon } from "@ui/components/icons";
import { Bloc, Row } from "@ui/components/layout";
import { PdfViewer } from "@ui/components/media/pdf/pdfViewer";
import { Page } from "@ui/components/page";
import { CarouselAction } from "@ui/components/pdfCarousel/carouselAction";
import { CarouselInfosView } from "@ui/components/pdfCarousel/carouselInfosView";
import { CarouselPdfViewer } from "@ui/components/pdfCarousel/carouselPdfViewer";
import { CarouselSuggestions } from "@ui/components/pdfCarousel/carouselSuggestions";
import { CarouselSuggestionsByCategory } from "@ui/components/pdfCarousel/carouselSuggestionsByCategory";
import { CarouselThumbnailMenu } from "@ui/components/pdfCarousel/carouselThumbnailMenu";
import { theme } from "@ui/styles/theme";
import { alpha, mobile } from "@ui/styles/utils";
import { NotFoundPage } from "@ui/pages/notFoundPage";

const PDF_HEIGHT = 800;
const DO_COUNT_VIEW_TIME = 10000;
type CarouselPageProps =
	| (React.HTMLAttributes<HTMLDivElement> & {
			carouselId: string;
			carouselSlug?: undefined;
			action?: "NEW_CAROUSEL_EMAIL" | "NEW_CAROUSEL_INSTRUCTIVE_EMAIL";
	  })
	| {
			carouselSlug: string;
			carouselId?: undefined;
			action?: "NEW_CAROUSEL_EMAIL" | "NEW_CAROUSEL_INSTRUCTIVE_EMAIL";
	  };

interface CarouselFormPageProps extends React.HTMLAttributes<HTMLDivElement> {
	carousel: Carousel;
	pdfHeight: number;
}

export const CarouselPage: React.FC<CarouselPageProps> = ({ carouselId, carouselSlug, action, ...htmlProps }) => {
	const originalIsMobile = useIsMobile();
	const { loading, result: carousel, error } = useCarousel(carouselId ?? "", carouselSlug);
	const { trackingService } = useServices();

	if ((!carouselId && !carouselSlug) || (error?.code === 404 && !carousel && !loading)) {
		return <NotFoundPage message={I18n.t("404.carousel")} />;
	}

	useEffect(() => {
		if (action === "NEW_CAROUSEL_EMAIL") {
			trackingService.track("trackEmail - NEW_CAROUSEL_EMAIL");
		}
		if (action === "NEW_CAROUSEL_INSTRUCTIVE_EMAIL") {
			trackingService.track("trackEmail - NEW_CAROUSEL_INSTRUCTIVE_EMAIL");
		}
	}, []);

	if (loading || !carousel)
		return (
			<Page>
				<ClipLoader />
			</Page>
		);

	return !originalIsMobile ? (
		<CarouselPageDesktop pdfHeight={PDF_HEIGHT} carousel={carousel} {...htmlProps} />
	) : (
		<CarouselPageMobile pdfHeight={PDF_HEIGHT} carousel={carousel} />
	);
};

export const CarouselPageDesktop: React.FC<CarouselFormPageProps> = ({ carousel, pdfHeight }) => {
	const pdfRow = useRef<HTMLDivElement>(null);
	const pdfBloc = useRef<HTMLDivElement>(null);
	const [pageWidth, setPageWidth] = useState(0);
	const { width: windowW, height: windowH } = useWindowSize();
	// const [scrollPosition, setScrollPosition] = useState(0);
	const [finalPageHeight, setFinalPageHeight] = useState(0);
	const [fullScreen, setFullScreen] = useState<boolean>();
	const currentProfile = useCurrentProfile();
	// const displayedAuthor = getDisplayedAuthor(carousel);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [finalPageHeight]);

	const handleScrollPdf = (): void => {
		setTimeout(() => {
			if (pdfRow.current) {
				const remainingMargin = pdfRow.current.scrollWidth - pdfRow.current.clientWidth;
				const showWings = remainingMargin > 0;
				if (showWings) pdfRow.current.scrollTo(remainingMargin / 2, remainingMargin / 2);
			}
		}, 500);
	};

	useEffect(() => {
		if (pdfRow.current) {
			pdfRow.current.addEventListener("scroll", () => handleScrollPdf());
		}
		return () => {
			if (pdfRow.current) pdfRow.current.removeEventListener("scroll", () => handleScrollPdf());
		};
	}, []);

	useEffect(() => {
		handleScrollPdf();
	}, [windowH, windowW, pageWidth, pdfRow.current]);

	const isAuthor = useIsPerson(carousel?.author ?? {});
	const { carouselService } = useServices();

	useOneCallTimer(
		DO_COUNT_VIEW_TIME,
		carousel && !isAuthor ? () => carouselService.incrementView(carousel) : undefined,
		[carousel]
	);

	const {
		asset: { url: source },
		id: carouselId,
	} = carousel;

	const history = useHistory();

	// const getWhatScroolToMinus = (): number => (isAuthor ? 310 : 213);

	return (
		<ContainerDesktop>
			<StyledBackButton>
				<StyledArrowIcon onClick={() => history.goBack()}>
					<ArrowIcon color={theme.colors.secondary} />
				</StyledArrowIcon>
				<BackTitle> {I18n.t("back")}</BackTitle>
			</StyledBackButton>
			<Row style={{ justifyContent: "space-between" }}>
				<Bloc ref={pdfBloc} style={{ overflow: "hidden", marginRight: 20 }}>
					<StyledPdfRow ref={pdfRow}>
						<CarouselPdfViewer
							fullScreenSetter={() => {
								setFullScreen(false);
							}}
							fullScreen={fullScreen}
							reportPageHeight={(value) => setFinalPageHeight(value)}
							source={source}
							pdfHeight={pdfHeight}
							reportPdfWith={(value) => setPageWidth(value)}
						/>
					</StyledPdfRow>

					<CarouselAction carousel={carousel} style={{ marginTop: 20 }} />
					<CarouselInfosView carousel={carousel} style={{ marginTop: 20 }} />
				</Bloc>
				<Bloc>
					{currentProfile && (
						<Row>
							{currentProfile && (
								<StyledMenu
									fullScreenSetter={() => {
										setFullScreen(true);
									}}
									fullScreenTop={fullScreen}
									carousel={carousel}
								/>
							)}
						</Row>
					)}
				</Bloc>
			</Row>

			<CarouselSuggestions carouselId={carouselId} categories={carousel.category} style={{ marginTop: 40 }} />
			<CarouselSuggestionsByCategory carouselId={carouselId} style={{ marginTop: 40 }} />

			{!currentProfile && <Space />}
			{!currentProfile && (
				<AppAuthIncentive
					message={I18n.t("carousel.auth.incentive", { category: I18n.t(`category.${carousel.category}`) })}
				/>
			)}
		</ContainerDesktop>
	);
};

export const CarouselPageMobile: React.FC<CarouselFormPageProps> = ({ carousel, pdfHeight, ...props }) => {
	const [fullScreen, setFullScreen] = useState<boolean>();
	// const containerRef = useRef<HTMLDivElement>(null);
	// const titleRow = useRef<HTMLDivElement>(null);
	const currentProfile = useCurrentProfile();

	const {
		asset: { url: source },
		id: carouselId,
	} = carousel;

	return (
		<ContainerMobile {...props}>
			<Row>
				{currentProfile && (
					<StyledMenu
						fullScreenSetter={() => {
							setFullScreen(true);
						}}
						fullScreenTop={fullScreen}
						carousel={carousel}
					/>
				)}
			</Row>

			<StyledPdfViewer
				fullScreenSetter={() => {
					setFullScreen(false);
				}}
				fullScreen={fullScreen}
				source={source}
				pdfHeight={pdfHeight}
			/>

			<StyledCarouselAction carousel={carousel} />
			<CarouselInfosView carousel={carousel} style={{ marginTop: 20 }} />

			<CarouselSuggestions carouselId={carouselId} categories={carousel.category} style={{ marginTop: 40 }} />

			{!currentProfile && <Space />}
			{!currentProfile && (
				<AppAuthIncentive
					message={I18n.t("carousel.auth.incentive", { category: I18n.t(`category.${carousel.category}`) })}
				/>
			)}
		</ContainerMobile>
	);
};

// Css Desktop
const ContainerDesktop = styled(Page)``;

const StyledPdfRow = styled(Row)`
	overflow: auto;
	max-width: "-webkit-fill-available";
	width: "-webkit-fill-available";
	::-webkit-scrollbar {
		width: 8px;
		height: 0px;
	}
	/* Track */
	::-webkit-scrollbar-track {
		box-shadow: inset 0 0 5px grey;
		border-radius: 10px;
	}
	/* Handle */
	::-webkit-scrollbar-thumb {
		background: #007aff;
		border-radius: 10px;
	}
	/* Handle on hover */
	::-webkit-scrollbar-thumb:hover {
		background: #b30000;
	}
`;

// Css Mobile
const ContainerMobile = styled(Page)``;

const StyledPdfViewer = styled(PdfViewer)``;

const Space = styled.div`
	height: 167px;
`;

const StyledMenu = styled(CarouselThumbnailMenu)`
	background-color: ${({ theme }) => alpha(theme.colors.backgroundPrimary, 0.98)};

	@media ${mobile} {
		position: absolute;
		z-index: 999;
		right: 20px;
		top: 20px;
	}
`;

const StyledCarouselAction = styled(CarouselAction)`
	margin-top: 20px;
	@media ${mobile} {
		margin-top: 10px;
	}

	button {
		margin-right: 0px;
	}
`;

const StyledArrowIcon = styled.div`
	width: 50px;
	height: 50px;
	border-radius: 50%;
	cursor: pointer;
	background-color: ${({ theme }) => theme.colors.white};
	display: flex;
	align-items: center;
	justify-content: center;
`;

const StyledBackButton = styled.div`
	display: flex;
	gap: 10px;
	align-items: center;
`;

const BackTitle = styled.div`
	${({ theme }) => theme.textStyles.title}
`;
